/**
 * entityRecordStore
 *
 * Module-private registry of Valtio stores keyed by `(entityId, recordId)`.
 * Each instance holds the live entity doc and live entity record doc, backed
 * by `persistDocument`. Multiple consumers of the same key share one instance
 * (refcounted). When the last consumer detaches, a grace timer (30s) gates
 * teardown so brief unmount/remount cycles don't churn Firestore subscriptions.
 *
 * `recordId` may be undefined to represent an "entity selected, no record"
 * scope — only the entity slot loads, the record slot stays empty.
 */
import { proxy } from 'valtio';
import type { Entity, EntityRecord } from '#types/System.types';
import type { DocumentPersist, DocumentState } from '../Store.types';
import { persistDocument } from '../persist/persistDocument';

const RECORD_NONE = '__none__';
const TEARDOWN_GRACE_MS = 30_000;

export type EntityRecordStoreState = {
    entityId: string;
    recordId: string | undefined;
    entityGroupId: string | undefined;
    entityLocked: boolean;
    entity: DocumentState<Entity>;
    entityRecord: DocumentState<EntityRecord>;
};

/**
 * Shared, never-mutated empty instance. Readers need an unconditional
 * `useSnapshot` target when no entity scope is bound; because nothing ever
 * writes to it, it never notifies and costs one subscription slot.
 */
export const EMPTY_ENTITY_RECORD_STATE: EntityRecordStoreState = proxy({
    entityId: '',
    recordId: undefined,
    entityGroupId: undefined,
    entityLocked: false,
    entity: { loading: false, data: null, error: null },
    entityRecord: { loading: false, data: null, error: null },
});

type EntityRecordStoreEntry = {
    state: EntityRecordStoreState;
    refCount: number;
    graceTimer: ReturnType<typeof setTimeout> | null;
    entityPersist: DocumentPersist<Entity> | null;
    entityUnsub: (() => void) | null;
    recordPersist: DocumentPersist<EntityRecord> | null;
    recordUnsub: (() => void) | null;
};

const instances = new Map<string, EntityRecordStoreEntry>();

/**
 * RecordIds whose store entry should mount unlocked (editing on) rather than the
 * default locked. Set by `createEntityRecord` so a just-created record opens
 * ready to edit; consumed once when the entry is first created (the record is
 * mounted lazily after navigation, so the flag bridges create → mount).
 */
const unlockOnMount = new Set<string>();

/** Mark a record to open unlocked when its store entry is next created. */
export const markRecordUnlockedOnMount = (recordId: string): void => {
    unlockOnMount.add(recordId);
};

const storeKey = (entityId: string, recordId: string | undefined): string =>
    `entities/${entityId}/records/${recordId ?? RECORD_NONE}`;

const createDocumentSlot = <T>(loading: boolean): DocumentState<T> => ({
    loading,
    data: null,
    error: null,
});

const setDocumentError = <T>(slot: DocumentState<T>) => (error: Error): void => {
    slot.error = error;
};

const connectEntity = (entry: EntityRecordStoreEntry, entityId: string): void => {
    const result = persistDocument<Entity>(
        `entities/${entityId}`,
        entry.state.entity,
        setDocumentError(entry.state.entity),
    );
    entry.entityPersist = result.persist;
    entry.entityUnsub = result.unsubscribe;
};

const connectRecord = (entry: EntityRecordStoreEntry, entityId: string, recordId: string): void => {
    const result = persistDocument<EntityRecord>(
        `entities/${entityId}/records/${recordId}`,
        entry.state.entityRecord,
        setDocumentError(entry.state.entityRecord),
    );
    entry.recordPersist = result.persist;
    entry.recordUnsub = result.unsubscribe;
};

const disconnect = (entry: EntityRecordStoreEntry): void => {
    entry.entityUnsub?.();
    entry.recordUnsub?.();
    entry.entityUnsub = null;
    entry.recordUnsub = null;
    entry.entityPersist = null;
    entry.recordPersist = null;
};

const createEntry = (entityId: string, recordId: string | undefined): EntityRecordStoreEntry => {
    const recordLoading = recordId !== undefined;
    const startUnlocked = recordId !== undefined && unlockOnMount.delete(recordId);
    const state = proxy<EntityRecordStoreState>({
        entityId,
        recordId,
        entityGroupId: undefined,
        entityLocked: !startUnlocked,
        entity: createDocumentSlot<Entity>(true),
        entityRecord: createDocumentSlot<EntityRecord>(recordLoading),
    });
    const entry: EntityRecordStoreEntry = {
        state,
        refCount: 0,
        graceTimer: null,
        entityPersist: null,
        entityUnsub: null,
        recordPersist: null,
        recordUnsub: null,
    };
    connectEntity(entry, entityId);
    if (recordId !== undefined) connectRecord(entry, entityId, recordId);
    return entry;
};

/**
 * Increments the refcount for `(entityId, recordId)`, creating the instance on
 * first acquire. Cancels any pending teardown timer.
 */
export const acquireEntityRecordStore = (
    entityId: string,
    recordId: string | undefined,
): EntityRecordStoreEntry => {
    const key = storeKey(entityId, recordId);
    let entry = instances.get(key);
    if (!entry) {
        entry = createEntry(entityId, recordId);
        instances.set(key, entry);
    } else if (entry.graceTimer) {
        clearTimeout(entry.graceTimer);
        entry.graceTimer = null;
    }
    entry.refCount += 1;
    return entry;
};

/**
 * Decrements the refcount for `(entityId, recordId)`. When the count reaches
 * zero, schedules teardown after `TEARDOWN_GRACE_MS`. A subsequent acquire
 * within the grace window cancels the timer and reuses the instance.
 */
export const releaseEntityRecordStore = (
    entityId: string,
    recordId: string | undefined,
): void => {
    const key = storeKey(entityId, recordId);
    const entry = instances.get(key);
    if (!entry) return;
    entry.refCount -= 1;
    if (entry.refCount > 0) return;
    if (entry.graceTimer) clearTimeout(entry.graceTimer);
    entry.graceTimer = setTimeout(() => {
        disconnect(entry);
        instances.delete(key);
    }, TEARDOWN_GRACE_MS);
};

/**
 * Tears down every live entity-record store: clears grace timers, disconnects
 * Firestore listeners, and empties the registry. Call on user signout to
 * prevent prior-user data and listeners from leaking into the next session.
 */
export const clearAllEntityRecordStores = (): void => {
    instances.forEach((entry) => {
        if (entry.graceTimer) {
            clearTimeout(entry.graceTimer);
            entry.graceTimer = null;
        }
        disconnect(entry);
        entry.refCount = 0;
    });
    instances.clear();
    unlockOnMount.clear();
};

/** Returns the store entry for the given key, or `null` if not currently mounted. */
export const getEntityRecordStoreEntry = (
    entityId: string,
    recordId: string | undefined,
): EntityRecordStoreEntry | null => instances.get(storeKey(entityId, recordId)) ?? null;

/** Returns the persist handle for the entity doc (writers). */
export const getEntityPersist = (
    entityId: string,
    recordId: string | undefined,
): DocumentPersist<Entity> | null =>
    getEntityRecordStoreEntry(entityId, recordId)?.entityPersist ?? null;

/** Returns the persist handle for the record doc (writers). */
export const getEntityRecordPersist = (
    entityId: string,
    recordId: string | undefined,
): DocumentPersist<EntityRecord> | null =>
    getEntityRecordStoreEntry(entityId, recordId)?.recordPersist ?? null;
